import { useState, useEffect, useRef } from "react";
import { getApiUrl } from "../config";
import { submitForm as sendFormEmail } from "../utils/formSubmit";

// ─── CONFIGURATION ──────────────────────────────────────────────
const PHONE_US = "17164577657";
const PHONE_US_DISPLAY = "(716) 457-7657";
const PHONE_CA = "16477385707";
const PHONE_CA_DISPLAY = "(647) 738-5707";
const WHATSAPP_NUMBER = "17164577657";

const API_URL = getApiUrl("/chat");

const MIN_REPLY_DELAY_MS = 2500;
const INACTIVITY_NUDGE_MS = 20000;
const MAX_NUDGES = 2;

const GREETING = "Assalamu Alaikum! Who's looking to learn Quran? 🌙";
// ────────────────────────────────────────────────────────────────

const QUICK_OPTIONS = [
  "I myself want to learn Quran",
  "My kids want to learn Quran",
  "I want to memorize the Quran",
];

const COURSES = ["Noorani Qaida", "Quran Reading", "Tajweed", "Hifz (Memorization)", "Translation", "Not sure yet"];

const BASE_SYSTEM = `You are Amina from Al Quran Lesson, chatting live with a website visitor. You are texting like a real human team member on a live chat widget — NOT writing an essay, NOT an AI assistant giving explanations.

═══════════════════════════════
ABSOLUTE RULES — BREAKING THESE RUINS THE CONVERSATION
═══════════════════════════════
1. You have ALREADY greeted the visitor. NEVER greet again. Never say "Assalamu Alaikum" a second time. Never re-introduce yourself.

2. A section below titled "CONVERSATION SO FAR" contains the full transcript of this chat. READ IT CAREFULLY BEFORE EVERY REPLY. It is the source of truth. Never ask for anything already answered there.
   - If they said "my kids" — you know who it's for. Never ask again.
   - If they said an age — you know the age. Never ask again.
   - If they said "male teacher" or "female teacher" — preference captured. Never ask again.
   - If they gave a name (even a bare one-word reply like "ali" right after you asked for a name — that IS their name) — never ask for their name again.
   - Same for phone, timezone, course.

3. A section titled "INFO ALREADY CAPTURED" lists what you definitely have. Never re-ask any item listed there.

4. Before replying, check: what do I already know, and what is the ONE next missing thing? Ask only that.

5. NEVER repeat your own previous message. If you asked something and they replied, acknowledge briefly and move to the NEXT thing. If you catch yourself about to send the same question twice, you are wrong — move forward instead.

6. Use their name sparingly — once or twice in the whole chat, not every message.

═══════════════════════════════
SOUND HUMAN, BE SHORT
═══════════════════════════════
Real people on live chat write ONE short sentence, sometimes two. No explanations unless asked.

BAD: "Great! Al Quran Lesson has been teaching since 2013 with over 5000 students and 150 certified tutors..."
GOOD: "Perfect — male or female tutor?"

Never more than 2 short sentences per reply.

═══════════════════════════════
INFORMATION TO COLLECT (one at a time, SKIP anything already captured)
═══════════════════════════════
- Who's learning (themselves / kids) + rough age if kids
- Beginner or has read Quran before
- Male or female tutor preference
- Their NAME
- Their PHONE NUMBER (required)
- Preferred day, time, timezone for trial
- Their email (last, for confirmation)

═══════════════════════════════
ONLY ONLINE CLASSES — NEVER MENTION IN-PERSON
═══════════════════════════════
All classes are 100% online, live, one-on-one via Zoom/Teams. No in-person option. If someone asks for a "local teacher" or teacher "near me," they still mean online — say everything is online and you'll check their timezone so the team matches a tutor whose availability fits. Never mention offices or physical locations.

═══════════════════════════════
PHONE NUMBER IS REQUIRED
═══════════════════════════════
Required to schedule anything — email alone is not enough. Never say "the team will text you" until you actually have a phone number.

IF THEY CAN'T/WON'T GIVE A NUMBER: one short warm line, then the marker [SHOW_FORM].
IF THEY'LL ONLY GIVE EMAIL: briefly say email works for info, but since they're already on the site everything's there — to actually book a trial the team needs to text or call to match the right tutor. Ask which they'd prefer.
IF THEY REFUSE EVERYTHING: ask once if they'd like a live agent. If still no, include [SHOW_FORM].

When you receive a piece of info for the FIRST time, include the matching hidden marker in that same reply (invisible to visitor):
[NAME:TheirName]  [PHONE:theirnumber]  [EMAIL:their@email.com]

═══════════════════════════════
SCHEDULING — NEVER CONFIRM A TIME YOURSELF
═══════════════════════════════
You don't have tutor schedules. Ask their preferred day/time/timezone, then say the team will check availability and confirm back. Never say "you're booked for X."

═══════════════════════════════
LIVE AGENT REQUESTS
═══════════════════════════════
If they ask for a human: reassure once briefly. If they ask again, include [SHOW_PHONE].

═══════════════════════════════
FACTS (mention only what's asked, never dump)
═══════════════════════════════
- Founded 2013, 5,000+ students, 150+ certified tutors, 4.6★
- Tutors mainly in Canada, Saudi Arabia, Pakistan, some US states — all fluent English
- 3-day free trial, no card needed
- Pricing (exact only): $25 = 1x/week · $35 = 2x/week · $45 = weekends only · $48 = 3x/week (most popular) · $65 = 4x/week · $80 = 5x/week. All 30-min, include progress reports, certificate, 24/7 support. 10% off 2nd sibling.
- Only quote the tier relevant to what they asked

═══════════════════════════════
HANDLING SILENCE
═══════════════════════════════
If the latest message is "[SYSTEM_NUDGE]" the visitor has gone quiet. Reply with ONE very short warm check-in that gently re-asks your last unanswered question in different words. Never mention the system note.`;

// Builds the system prompt fresh each turn, embedding the full transcript and
// captured lead info. This guarantees context survives even if the backend
// only forwards the latest message.
function buildSystemPrompt(history, lead) {
  const transcript = history
    .map(m => (m.role === "user" ? "Visitor: " : "Amina: ") + m.content)
    .join("\n");

  const captured = [];
  if (lead.name) captured.push(`- NAME: ${lead.name} (already have it — never ask again)`);
  if (lead.phone) captured.push(`- PHONE: ${lead.phone} (already have it — never ask again)`);
  if (lead.email) captured.push(`- EMAIL: ${lead.email} (already have it — never ask again)`);

  const capturedBlock = captured.length
    ? `\n\n═══════════════════════════════\nINFO ALREADY CAPTURED — NEVER RE-ASK THESE\n═══════════════════════════════\n${captured.join("\n")}`
    : "";

  return `${BASE_SYSTEM}${capturedBlock}

═══════════════════════════════
CONVERSATION SO FAR (full transcript — read before replying)
═══════════════════════════════
${transcript || "(no messages yet)"}

Now write Amina's next reply. It must move the conversation FORWARD — do not repeat any question already asked above.`;
}

function parseMarkers(text, leadRef, setLeadCaptured, setShowForm, setShowPhone) {
  let clean = text;
  const updated = { ...leadRef.current };
  let changed = false;

  const nameMatch = clean.match(/\[NAME:([^\]]+)\]/);
  if (nameMatch) {
    const candidateName = nameMatch[1].trim();
    if (!candidateName.includes("@") && !candidateName.includes(".com") && !/^\d+$/.test(candidateName)) {
      updated.name = candidateName;
      changed = true;
    }
    clean = clean.replace(nameMatch[0], "");
  }

  const phoneMatch = clean.match(/\[PHONE:([^\]]+)\]/);
  if (phoneMatch) { updated.phone = phoneMatch[1].trim(); clean = clean.replace(phoneMatch[0], ""); changed = true; }

  const emailMatch = clean.match(/\[EMAIL:([^\]]+)\]/);
  if (emailMatch) { updated.email = emailMatch[1].trim(); clean = clean.replace(emailMatch[0], ""); changed = true; }

  if (clean.includes("[SHOW_FORM]")) {
    clean = clean.replace("[SHOW_FORM]", "").trim();
    setShowForm(true);
  }
  if (clean.includes("[SHOW_PHONE]")) {
    clean = clean.replace("[SHOW_PHONE]", "").trim();
    setShowPhone(true);
  }

  if (changed) {
    leadRef.current = updated;
    if (updated.phone && (updated.name || updated.email)) setLeadCaptured(true);
  }

  return clean.replace(/\s{2,}/g, " ").trim();
}

export default function AlQuranLessonChatbot() {
  const [messages, setMessages] = useState([{ role: "bot", text: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [leadCaptured, setLeadCaptured] = useState(false);

  const [formName, setFormName] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formCourse, setFormCourse] = useState(COURSES[0]);

  const historyRef = useRef([{ role: "assistant", content: GREETING }]);
  const leadRef = useRef({ name: "", phone: "", email: "", interest: "" });
  const lastSentLeadRef = useRef({ name: "", phone: "", email: "" });
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const nudgeCountRef = useRef(0);

  useEffect(() => {
    if (leadCaptured) {
      const lead = leadRef.current;
      if (!lead.name || !lead.phone) return;

      if (
        lastSentLeadRef.current.name === lead.name &&
        lastSentLeadRef.current.phone === lead.phone &&
        lastSentLeadRef.current.email === lead.email
      ) {
        return;
      }

      lastSentLeadRef.current = { name: lead.name, phone: lead.phone, email: lead.email };

      sendFormEmail("Chatbot Lead Captured", {
        name: lead.name || "Not provided",
        phone: lead.phone || "Not provided",
        email: lead.email || "Not provided",
        courseInterest: lead.interest || "Quran Classes",
        chatTime: new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
      }).catch(err => console.error("Chatbot lead email error:", err));
    }
  }, [leadCaptured, messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [messages, loading, showForm, isOpen]);

  function clearInactivityTimer() {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }

  function armInactivityTimer() {
    clearInactivityTimer();
    // Do not nudge if the visitor hasn't started the conversation yet
    const hasUserStarted = historyRef.current.some(m => m.role === "user");
    if (!hasUserStarted) return;
    if (nudgeCountRef.current >= MAX_NUDGES) return;
    inactivityTimerRef.current = setTimeout(() => { sendNudge(); }, INACTIVITY_NUDGE_MS);
  }

  useEffect(() => {
    return () => clearInactivityTimer();
  }, []);

  // Sends BOTH the full messages array AND the transcript inside the system
  // prompt, so context survives regardless of how the backend handles history.
  async function callAPI(historyPayload) {
    try {
      const systemPrompt = buildSystemPrompt(historyPayload, leadRef.current);
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: systemPrompt,
          messages: historyPayload.filter(m => m.role === "user" || m.role === "assistant"),
        }),
      });
      const data = await res.json();
      if (data?.error) {
        console.error("API error:", data.error);
        return null;
      }
      return data?.content?.[0]?.text || null;
    } catch (err) {
      console.error("API call exception:", err);
      return null;
    }
  }

  async function sendNudge() {
    const hasUserStarted = historyRef.current.some(m => m.role === "user");
    if (!hasUserStarted) return;

    nudgeCountRef.current += 1;
    const nudgePayload = [...historyRef.current, { role: "user", content: "[SYSTEM_NUDGE]" }];
    try {
      let reply = await callAPI(nudgePayload);
      if (!reply) return;
      reply = parseMarkers(reply, leadRef, setLeadCaptured, setShowForm, setShowPhone);
      historyRef.current = [...historyRef.current, { role: "assistant", content: reply }];
      setMessages(prev => [...prev, { role: "bot", text: reply }]);
      armInactivityTimer();
    } catch {
      // silent
    }
  }

  async function sendMessage(overrideText) {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setShowQuickOptions(false);
    setLoading(true);
    clearInactivityTimer();

    setMessages(prev => [...prev, { role: "user", text }]);
    const updatedHistory = [...historyRef.current, { role: "user", content: text }];
    historyRef.current = updatedHistory;

    if (!leadRef.current.interest && text.length > 3) {
      leadRef.current.interest = text;
    }

    // Direct client-side lead capture
    const lastBotTurn = [...historyRef.current].reverse().find(m => m.role === "assistant");
    const lastBotText = (lastBotTurn?.content || "").toLowerCase();

    // Capture Name if previous bot asked for name (and text is not an email or phone)
    if (!leadRef.current.name && (lastBotText.includes("name") || lastBotText.includes("who's looking"))) {
      if (!text.includes("@") && !/\d{5,}/.test(text)) {
        const cleanedName = text.replace(/[0-9+()\-@,.:;!?]/g, '').trim();
        const ignore = ['yes', 'no', 'male', 'female', 'kids', 'child', 'myself', 'sure', 'ok', 'okay', 'hi', 'hello', 'salam'];
        if (cleanedName && cleanedName.length >= 2 && !ignore.includes(cleanedName.toLowerCase())) {
          leadRef.current.name = cleanedName.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        }
      }
    }

    // Capture Phone if text contains phone format
    const phoneMatch = text.match(/(?:\+?\d{1,4}[-.\s]*)?(?:\(?\d{2,4}\)?[-.\s]*)?\d{3,4}[-.\s]*\d{3,5}/);
    if (phoneMatch) {
      const digits = phoneMatch[0].replace(/\D/g, '');
      if (digits.length >= 7 && digits.length <= 15) {
        leadRef.current.phone = phoneMatch[0].trim();
      }
    }

    // Capture Email if text contains email format
    const emailMatch = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    if (emailMatch) {
      leadRef.current.email = emailMatch[0].trim();
    }

    if (leadRef.current.name && leadRef.current.phone) {
      setLeadCaptured(true);
    }

    const requestStart = Date.now();

    try {
      let reply = await callAPI(updatedHistory);
      if (!reply) reply = "Sorry, having trouble connecting — please text us at " + PHONE_US_DISPLAY;

      reply = parseMarkers(reply, leadRef, setLeadCaptured, setShowForm, setShowPhone);

      const remaining = MIN_REPLY_DELAY_MS - (Date.now() - requestStart);
      if (remaining > 0) await new Promise(r => setTimeout(r, remaining));

      historyRef.current = [...historyRef.current, { role: "assistant", content: reply }];
      setMessages(prev => [...prev, { role: "bot", text: reply }]);
      nudgeCountRef.current = 0;
      armInactivityTimer();
    } catch (err) {
      console.error("Chat error:", err);
      setMessages(prev => [...prev, { role: "bot", text: "Connection hiccup — text us at " + PHONE_US_DISPLAY }]);
    }

    setLoading(false);
    inputRef.current?.focus();
  }

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  async function submitForm() {
    if (!formName.trim() || !formPhone.trim()) return;
    leadRef.current = { ...leadRef.current, name: formName.trim(), phone: formPhone.trim(), interest: formCourse };

    try {
      await sendFormEmail("Chatbot Direct Form Submission", {
        name: formName.trim(),
        phone: formPhone.trim(),
        email: leadRef.current.email || "Not provided",
        courseInterest: formCourse,
        chatTime: new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
      });
    } catch (err) {
      console.error("Chatbot direct form submit error:", err);
    }

    setFormSubmitted(true);
    setShowForm(false);
    const confirmLine = "Got it, thank you! Our team will text you shortly to set up your trial 🙂";
    historyRef.current = [
      ...historyRef.current,
      { role: "user", content: `[FORM SUBMITTED] Name: ${formName.trim()}, Phone: ${formPhone.trim()}, Course: ${formCourse}` },
      { role: "assistant", content: confirmLine },
    ];
    setMessages(prev => [...prev, { role: "bot", text: confirmLine }]);
  }

  const s = {
    wrap: { position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12, fontFamily: "'Segoe UI', system-ui, sans-serif" },
    window: { width: 360, height: isOpen ? 560 : 0, opacity: isOpen ? 1 : 0, overflow: "hidden", borderRadius: 16, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", background: "#faf8f3", transition: "height 0.3s ease, opacity 0.25s ease", border: "1px solid rgba(0,0,0,0.08)" },
    header: { background: "#0f5132", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },
    avatar: { width: 36, height: 36, borderRadius: "50%", background: "#d4af37", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 600, color: "#0f5132", flexShrink: 0 },
    headerName: { color: "#fff", fontSize: 13, fontWeight: 600, margin: 0, lineHeight: 1.3 },
    headerSub: { color: "#bde5cf", fontSize: 11, margin: 0 },
    dot: { width: 8, height: 8, borderRadius: "50%", background: "#5dcaa5", marginLeft: "auto" },
    messages: { flex: 1, overflowY: "auto", padding: "14px 12px", display: "flex", flexDirection: "column", gap: 10 },
    botRow: { display: "flex", gap: 8, alignItems: "flex-end", maxWidth: "88%" },
    userRow: { display: "flex", justifyContent: "flex-end", alignSelf: "flex-end", maxWidth: "88%" },
    botAvatar: { width: 26, height: 26, borderRadius: "50%", background: "#0f5132", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: "#d4af37", flexShrink: 0 },
    botBubble: { background: "#fff", border: "1px solid rgba(0,0,0,0.07)", borderRadius: "14px 14px 14px 3px", padding: "9px 12px", fontSize: 13.5, lineHeight: 1.5, color: "#1a1a1a" },
    userBubble: { background: "#0f5132", borderRadius: "14px 14px 3px 14px", padding: "9px 12px", fontSize: 13.5, lineHeight: 1.5, color: "#fff" },
    quickOptsWrap: { display: "flex", flexDirection: "column", gap: 6, marginTop: 4, marginLeft: 34 },
    quickOptBtn: { background: "#fff", border: "1.5px solid #0f5132", color: "#0f5132", borderRadius: 16, padding: "7px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", textAlign: "left" },
    phoneBar: { background: "#fdf6e3", borderTop: "1px solid #e8d9a0", padding: "10px 14px", display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 },
    phoneRow: { display: "flex", gap: 6, flexWrap: "wrap" },
    callBtn: { background: "#0f5132", color: "#fff", borderRadius: 8, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, textDecoration: "none", display: "inline-block" },
    waBtn: { background: "#25D366", color: "#fff", borderRadius: 8, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, textDecoration: "none", display: "inline-block" },
    formWrap: { background: "#fff", borderTop: "1px solid rgba(0,0,0,0.08)", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 },
    formLabel: { fontSize: 11, color: "#555", fontWeight: 600 },
    formInput: { border: "1.5px solid #dde1e7", borderRadius: 8, padding: "7px 10px", fontSize: 13, outline: "none", fontFamily: "inherit" },
    formSelect: { border: "1.5px solid #dde1e7", borderRadius: 8, padding: "7px 10px", fontSize: 13, outline: "none", fontFamily: "inherit", background: "#fff" },
    formSubmitBtn: { background: "#0f5132", color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
    inputArea: { padding: "10px 12px", borderTop: "1px solid rgba(0,0,0,0.07)", display: "flex", gap: 8, alignItems: "center", background: "#fff", flexShrink: 0 },
    input: { flex: 1, border: "1.5px solid #dde1e7", borderRadius: 20, padding: "8px 14px", fontSize: 13.5, outline: "none", fontFamily: "inherit", background: "#f7f8fa", color: "#1a1a1a" },
    sendBtn: { width: 34, height: 34, borderRadius: "50%", background: loading ? "#ccc" : "#0f5132", border: "none", cursor: loading ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    footer: { padding: "6px", textAlign: "center", fontSize: 10.5, color: "#aaa", background: "#fff", borderTop: "1px solid rgba(0,0,0,0.05)" },
    fab: { width: 52, height: 52, borderRadius: "50%", background: "#0f5132", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.2)" },
  };

  return (
    <div style={s.wrap}>
      <div style={s.window}>
        <div style={s.header}>
          <div style={s.avatar}>☪</div>
          <div style={{ flex: 1 }}>
            <p style={s.headerName}>Al Quran Lesson</p>
            <p style={s.headerSub}>Online Quran Academy • Est. 2013</p>
          </div>
          <div style={s.dot} />
        </div>

        <div style={s.messages}>
          {messages.map((m, i) =>
            m.role === "bot" ? (
              <div key={i} style={s.botRow}>
                <div style={s.botAvatar}>AM</div>
                <div style={s.botBubble}>{m.text}</div>
              </div>
            ) : (
              <div key={i} style={s.userRow}>
                <div style={s.userBubble}>{m.text}</div>
              </div>
            )
          )}

          {showQuickOptions && !loading && messages.length === 1 && (
            <div style={s.quickOptsWrap}>
              {QUICK_OPTIONS.map((opt, idx) => (
                <button key={idx} style={s.quickOptBtn} onClick={() => sendMessage(opt)}>{opt}</button>
              ))}
            </div>
          )}

          {loading && (
            <div style={s.botRow}>
              <div style={s.botAvatar}>AM</div>
              <div style={s.botBubble}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#aaa", animation: "aqlBounce 1.2s infinite", animationDelay: `${i * 0.2}s` }} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {showForm && !formSubmitted && (
          <div style={s.formWrap}>
            <div style={s.formLabel}>Quick details so our team can text you:</div>
            <input style={s.formInput} placeholder="Your name" value={formName} onChange={e => setFormName(e.target.value)} />
            <input style={s.formInput} placeholder="Your phone number" value={formPhone} onChange={e => setFormPhone(e.target.value)} />
            <select style={s.formSelect} value={formCourse} onChange={e => setFormCourse(e.target.value)}>
              {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button style={s.formSubmitBtn} onClick={submitForm}>Submit</button>
          </div>
        )}

        {showPhone && (
          <div style={s.phoneBar}>
            <div style={{ fontSize: 11, color: "#8a6d1a", fontWeight: 600 }}>Talk to our live team:</div>
            <div style={s.phoneRow}>
              <a href={`tel:${PHONE_US}`} style={s.callBtn}>📞 {PHONE_US_DISPLAY}</a>
              <a href={`tel:${PHONE_CA}`} style={s.callBtn}>📞 {PHONE_CA_DISPLAY}</a>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" style={s.waBtn}>💬 WhatsApp</a>
            </div>
          </div>
        )}

        <div style={s.inputArea}>
          <input ref={inputRef} style={s.input} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} placeholder="Type a message..." disabled={loading} />
          <button style={s.sendBtn} onClick={() => sendMessage()} disabled={loading}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <div style={s.footer}>Al Quran Lesson</div>
      </div>

      <button style={s.fab} onClick={() => setIsOpen(o => !o)}>
        {isOpen ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>

      <style>{`@keyframes aqlBounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }`}</style>
    </div>
  );
}
